import {Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {of, Subject} from 'rxjs';
import {catchError, takeUntil} from 'rxjs/operators';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {BidMapRegionAggregateDto} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidMapRegionAggregateDto';
import {RegionService} from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import {Region as ApiRegion} from '@6grain/masofa-monolith-api-client/dictionaries/model/region';
import {ModalService} from '@services/modal.service';
import {RiskDataService} from '@services/risk-data.service';
import {ServerTranslateService} from '@services/server-translate.service';
import {DataTableComponent, TableColumn, TableConfig} from '@components/data-table/data-table.component';
import {Loading} from '@components/ui/loading/loading';
import {UnitTranslatePipe} from '@app/utils/pipes/unit-translate-pipe';

interface Region {
  name: string;
  count: number;
  id: string;
  area?: number;
}

@Component({
  selector: 'app-problematic-areas',
  standalone: true,
  imports: [CommonModule, TranslatePipe, DataTableComponent, Loading, UnitTranslatePipe],
  templateUrl: './problematic-areas.component.html',
  styleUrl: './problematic-areas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProblematicAreasComponent implements OnInit, OnDestroy, AfterViewInit {
  loading = true;
  regions: Region[] = [];
  totalRegions = 0;
  totalArea = 0;
  percentage = 0;
  regionMap = new Map<string, ApiRegion>();
  private destroy$ = new Subject<void>();

  tableColumns: TableColumn[] = [];
  tableConfig: TableConfig = {
    selectable: false,
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    loading: false,
    emptyMessage: 'No data found',
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private bidService: BidService,
    private regionService: RegionService,
    private modalService: ModalService,
    private riskDataService: RiskDataService,
    private translate: TranslateService,
    private serverTranslate: ServerTranslateService,
  ) {}

  ngOnInit() {
    this.loadRegions();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.regionMap.size > 0) {
        this.loadData();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRegions() {
    this.regionService.dictionariesRegionGetByQueryPost({take: 1000, offset: 0}).subscribe({
      next: regions => {
        if (regions && Array.isArray(regions)) {
          regions.forEach(region => {
            if (region.id) {
              this.regionMap.set(region.id, region);
            }
          });
        }
        this.loadData();
      },
      error: err => {
        console.error('Error loading regions:', err);
        this.loadData();
      },
    });
  }

  /**
   * “Request by region” — same source as GIS bids regional aggregates:
   * {@link BidService.cropMonitoringBidGetMapRegionAggregatesPost} (`/cropMonitoring/Bid/GetMapRegionAggregates`).
   * Display names: API `regionNames`, else dictionary ({@link RegionService}) as in region management.
   */
  private loadData() {
    this.loading = true;

    this.bidService
      .cropMonitoringBidGetMapRegionAggregatesPost({query: {}})
      .pipe(
        catchError(err => {
          console.error('Error loading bid map region aggregates:', err);
          return of([] as BidMapRegionAggregateDto[]);
        }),
      )
      .subscribe(aggregates => {
        const cropMonitoringByRegion = new Map<string, number>();
        const nameHints = new Map<string, Record<string, string>>();

        for (const row of aggregates ?? []) {
          const n = row.bidsCount ?? 0;
          if (n < 1) {
            continue;
          }
          const regionKey =
            row.regionId != null && String(row.regionId).trim() ? String(row.regionId).trim() : '';
          if (!regionKey) {
            continue;
          }

          let aggId = regionKey;
          const resolved = this.resolveLevel2Region(regionKey);
          if (resolved?.id) {
            aggId = String(resolved.id);
          }

          const rn = row.regionNames as Record<string, string> | null | undefined;
          if (rn && typeof rn === 'object' && !nameHints.has(aggId)) {
            nameHints.set(aggId, rn);
          }

          cropMonitoringByRegion.set(aggId, (cropMonitoringByRegion.get(aggId) ?? 0) + n);
        }

        this.regions = Array.from(cropMonitoringByRegion.entries())
          .map(([id, count]) => ({
            id,
            name: this.displayNameForRegionId(id, nameHints.get(id)),
            count,
          }))
          .sort((a, b) => b.count - a.count);

        this.totalArea = this.regions.reduce((s, r) => s + r.count, 0);
        this.totalRegions = this.regions.length;
        this.percentage = 0;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  private resolveLevel2Region(regionId: string): ApiRegion | undefined {
    const region = this.regionMap.get(regionId);
    if (!region) {
      return undefined;
    }
    if (Number(region.level) === 2) {
      return region;
    }
    if (Number(region.level) === 3 && region.parentId) {
      return this.regionMap.get(String(region.parentId));
    }
    return region;
  }

  private displayNameForRegionId(
    regionId: string,
    namesHint?: Record<string, string>,
  ): string {
    const fromMap = this.regionMap.get(regionId);
    if (fromMap) {
      return this.getRegionDisplayName(fromMap);
    }
    const picked = this.pickRegionNamesRecord(namesHint);
    if (picked) {
      return picked;
    }
    return regionId || '—';
  }

  private pickRegionNamesRecord(names?: Record<string, string> | null): string {
    if (!names || typeof names !== 'object') {
      return '';
    }
    const lang = this.translate.currentLang || 'en';
    const short = lang.split('-')[0] ?? 'en';
    return (
      names[lang] ||
      names[short] ||
      names['en-US'] ||
      names['en'] ||
      names['ru-RU'] ||
      names['ru'] ||
      (Object.values(names).find(v => v && String(v).trim()) as string) ||
      ''
    );
  }

  private getRegionDisplayName(region: ApiRegion): string {
    return (this.serverTranslate.translate(region, 'names') as string) || region.nameMhobt || '—';
  }

  onRegionClick(region: Region) {
    this.riskDataService.getRiskDataForRegion(region.name, region.id).subscribe(riskData => {
      this.modalService.showRiskModal(riskData);
    });
  }

  ngAfterViewInit(): void {
    this.tableColumns = [
      {
        field: 'name',
        header: 'Region',
        defaultSorted: true,
        sortable: false,
      },
      {
        field: 'count',
        header: 'Number of requests',
        type: 'number',
        sortable: false,
      },
    ];
  }

  onFilter($event: any) {
    console.log($event);
  }

  onSelectAllChange($event: boolean) {
    console.log($event);
  }

  onSort($event: any) {
    console.log($event);
  }

  onPageChange($event: any) {
    console.log($event);
  }

  onRowUnselect($event: any) {
    console.log($event);
  }

  onRowSelect($event: any) {
    console.log($event);
  }

  onRowClick($event: any) {
    this.onRegionClick($event);
  }
}
